import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, BadgeCheck, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg } from "../lib/api";
import { CHURCH } from "../lib/data";

export default function CardVerify() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    api.get(`/staff/card/${code}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Card not found or you don't have permission."));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  const checkIn = async (ticketCode) => {
    try {
      await api.post(`/paypal/tickets/checkin/${ticketCode}`);
      toast.success("Checked in!");
      load();
    } catch { toast.error("Check-in failed"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-5 py-16" data-testid="card-verify-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={CHURCH.logo} alt="" className="w-14 h-14 object-contain mx-auto mb-3" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#E63946]">Membership Card Verification</p>
        </div>
        {!data && !err && <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" size={32} /></div>}
        {err && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-testid="card-verify-error">
            <XCircle className="text-[#E63946] mx-auto mb-3" size={40} />
            <p className="font-display font-bold text-lg">{err}</p>
            <p className="text-white/40 text-sm mt-2">Staff must be signed in at /admin/login to verify membership cards.</p>
          </div>
        )}
        {data && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-7" data-testid="card-verify-result">
            <div className="flex items-center gap-4">
              {data.photo ? <img src={resolveImg(data.photo)} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white/20" /> :
                <div className="w-16 h-16 rounded-full bg-[#E63946]/20 grid place-items-center font-display font-bold text-xl text-[#E63946]">{(data.name || "?")[0]}</div>}
              <div>
                <p className="font-display font-bold text-2xl">{data.name}</p>
                <p className="text-sm text-white/50 font-mono">{data.member_id}</p>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 rounded-xl p-3.5">
              <BadgeCheck size={20} className="text-emerald-400" />
              <p className="font-medium" data-testid="card-status">{data.membership_status}</p>
              {data.member_since && <p className="text-xs text-white/40 ml-auto">since {new Date(data.member_since).getFullYear()}</p>}
            </div>
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Event Registrations</p>
              {data.tickets.length === 0 && <p className="text-white/40 text-sm">No event tickets on file.</p>}
              <div className="space-y-2">
                {data.tickets.map((t) => (
                  <div key={t.ticket_code} className="flex items-center gap-3 bg-white/[0.04] border border-white/10 rounded-xl p-3.5" data-testid="card-ticket-row">
                    <Ticket size={16} className={t.checked_in ? "text-emerald-400" : "text-[#E63946]"} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t.event_title}</p>
                      <p className="text-xs text-white/40">{t.quantity} ticket{t.quantity > 1 ? "s" : ""} · <span className="font-mono">{t.ticket_code}</span></p>
                    </div>
                    {t.checked_in ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 size={13} /> Checked in</span>
                    ) : (
                      <button onClick={() => checkIn(t.ticket_code)} data-testid="card-checkin-btn"
                        className="rounded-full bg-[#E63946] px-4 py-2 text-xs font-medium hover:bg-[#BA1B25]">Check In</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-white/25 mt-5">Private information (giving, purchases, applications) is never shown on card scans.</p>
          </div>
        )}
      </div>
    </div>
  );
}
